import { useEffect, useState } from "react";
import { ChevronRight, Send } from "lucide-react";

// Frases que rotan sobre el cuadro del agente general (cambian en cada visita y solas cada pocos segundos).
export const PHRASES = [
  "¿Qué vamos a construir hoy?",
  "¿Qué idea tienes hoy?",
  "¿En qué trabajamos hoy?",
  "¿Qué se te ocurre hoy?",
  "Hoy es buen día para lanzar algo nuevo",
  "¿Por dónde empezamos hoy?",
];
export const ROTATE_MS = 7000;
const FADE_MS = 300;

function useRotatingPhrase() {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * PHRASES.length));
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    let reduce = false;
    try {
      reduce = !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch (e) {
      reduce = false;
    }
    if (reduce) return undefined; // quien pidió menos movimiento ve una sola frase por visita
    let swap;
    const timer = setInterval(() => {
      setVisible(false);
      swap = setTimeout(() => {
        setIndex((current) => (current + 1) % PHRASES.length);
        setVisible(true);
      }, FADE_MS);
    }, ROTATE_MS);
    return () => {
      clearInterval(timer);
      clearTimeout(swap);
    };
  }, []);
  return { phrase: PHRASES[index], visible };
}

// La ventanita del agente general en el inicio: escribes y se abre su chat completo; debajo, el último intercambio (toda esa zona abre el chat).
export function GeneralChatBox({ general, onSend, onContinue }) {
  const [text, setText] = useState("");
  const { phrase, visible } = useRotatingPhrase();
  const send = () => {
    const message = text.trim();
    if (!message) return;
    setText("");
    onSend(message);
  };
  const hasLast = !!(general?.last_user || general?.last_assistant);
  return (
    <section data-testid="general-box" className="mx-auto w-full max-w-2xl space-y-4">
      <h2
        data-testid="general-phrase"
        className={`text-center text-2xl sm:text-3xl font-semibold tracking-tight transition-opacity duration-300 ${visible ? "opacity-100" : "opacity-0"}`}
      >
        {phrase}
      </h2>
      <div className="rounded-2xl border border-emerald-500/25 bg-card/80 shadow-lg shadow-emerald-500/5 backdrop-blur transition-colors focus-within:border-emerald-500/60">
        <textarea
          data-testid="general-input"
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Pregúntale al Orquestador sobre tus proyectos, o pídele ayuda para diseñar uno nuevo…"
          aria-label="Escríbele al Orquestador"
          className="block w-full resize-none rounded-t-2xl bg-transparent px-4 pt-4 text-sm outline-none placeholder:text-muted-foreground"
        />
        <div className="flex items-center justify-between px-3 pb-3 pt-1">
          <span className="text-[11px] text-muted-foreground">Enter envía · Shift + Enter, línea nueva</span>
          <button
            type="button"
            data-testid="general-send"
            aria-label="Enviar"
            disabled={!text.trim()}
            onClick={send}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-black transition-opacity hover:bg-emerald-400 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
      {hasLast && (
        <button
          type="button"
          data-testid="general-last"
          onClick={onContinue}
          aria-label="Abrir la conversación con el Orquestador"
          className="group flex w-full items-center gap-3 rounded-xl border border-border/70 bg-muted/30 px-4 py-3 text-left transition-colors hover:border-emerald-500/40 hover:bg-muted/50"
        >
          <span className="min-w-0 flex-1 space-y-1 text-sm">
            {general.last_user && (
              <span data-testid="general-last-user" className="block truncate text-muted-foreground">
                <span className="font-medium text-foreground">Tú:</span> {general.last_user}
              </span>
            )}
            {general.last_assistant && (
              <span data-testid="general-last-assistant" className="line-clamp-2 block text-muted-foreground">
                <span className="font-medium text-foreground">🧠 Orquestador:</span> {general.last_assistant}
              </span>
            )}
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
        </button>
      )}
    </section>
  );
}
